import { ReactElement, useEffect, useRef, useState } from "react"
import { InspectorPanelAccordionKeyValueToggle } from "./InspectorPanelAccordionKeyValueToggle"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import { InspectorPanelAccordionSlider } from "./InspectorPanelAccordionSlider"
import { AnyAction } from "@nova/constants"
import { ToggleSwitch } from "@nova/component-toggle-switch"

type Child =
    | ReactElement<typeof InspectorPanelAccordionKeyValueToggle>
    | ReactElement<typeof InspectorPanelAccordionSlider>
    | false

type InspectorPanelAccordionProps = {
    title?: string
    subtitle?: string
    customHeaderComponent?: ReactElement

    isToggled?: boolean
    setIsToggled?: (isToggled: boolean) => void
    isHeaderToggleDisabled?: boolean

    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void

    children?: Child | Child[]

    /**
     * Designs call for a `py-2.5` in the accordion body, except for the
     * Export accordion. Set this to `false` if using for Export.
     * @default true
     */
    shouldPadChildrenVertically?: boolean

    /**
     * For the accordion to shrink to its contents, this value should be
     * provided specifically. The shrinkage tends to ignore padding/gaps,
     * so this should be the value *in REM* of that padding, accumulated.
     * @default 1.25
     */
    extraVerticalPaddingInRem?: number

    /**
     * Whether the children are subsections (otherwise, they are regular
     * accordion children). If true, this will remove the horizontal
     * padding and leave that logic to InspectorPanelAccordionSubsections.
     */
    hasSubsections?: boolean

    /**
     * Generally, the accordion header has a padding of 0.5rem. If there is
     * a subtitle, then it is 0.625rem (0.5 + a constant of 0.125). You can
     * adjust the Y-axis padding of the header here. This value is *in REM*!
     * @default 0.5
     */
    headerVerticalPaddingInRem?: number

    /**
     * If the `customHeaderComponent` has some absolutely positioned children
     * that may overflow outside of the header and into the accordion body,
     * then the accordion needs to know how to resize accordingly. As an example,
     * if a `ColorPalette` is a child of the header, we want the accordion to
     * expand its height if the custom color picker is open, but not when just the
     * color palette itself is open. So, in this prop, we'd rovide the height of
     * the normal color palette whenever the color palette is open.
     */
    headerExtraHeightCalculationInRem?: number

    extraResizeDependency?: AnyAction
    RightItem?: ReactElement

    className?: string
    mutedTitle?: boolean
}

export const InspectorPanelAccordion = ({
    title,
    subtitle,
    customHeaderComponent,
    isToggled,
    setIsToggled,
    isOpen,
    setIsOpen,
    children,
    shouldPadChildrenVertically = true,
    extraVerticalPaddingInRem = 1.25,
    hasSubsections = false,
    headerVerticalPaddingInRem = 0.5,
    headerExtraHeightCalculationInRem,
    extraResizeDependency,
    RightItem,
    className,
    isHeaderToggleDisabled,
    mutedTitle,
}: InspectorPanelAccordionProps) => {
    if (
        (isToggled !== undefined && setIsToggled === undefined) ||
        (isToggled === undefined && setIsToggled !== undefined)
    )
        throw "Must provide isToggled & setIsToggled, or neither."
    if ((!title && !customHeaderComponent) || (title && customHeaderComponent))
        throw "Must provide either a `title` or a `customHeaderComponent`, but not both."
    if (subtitle && customHeaderComponent)
        throw "Cannot provide a `subtitle` when the `customHeaderComponent` prop is"

    const headerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [contentHeight, setContentHeight] = useState(isOpen ? "auto" : 0)
    const [headerBaseHeight, setHeaderBaseHeight] = useState(0)

    useEffect(() => {
        setContentHeight(
            isOpen
                ? `calc(${contentRef.current?.scrollHeight}px + ${headerRef.current?.scrollHeight}px - ${headerExtraHeightCalculationInRem || 0}rem - ${headerBaseHeight}px + ${extraVerticalPaddingInRem}rem`
                : 0
        )
    }, [
        children,
        isOpen,
        extraVerticalPaddingInRem,
        extraResizeDependency,
        headerExtraHeightCalculationInRem,
        headerBaseHeight,
    ])

    useEffect(() => {
        setHeaderBaseHeight(headerRef.current?.scrollHeight || 0)
    }, [])

    const headerVerticalPadding = `${headerVerticalPaddingInRem + (subtitle ? 0.125 : 0)}rem`

    return (
        <div
            className={classNames(
                "mx-4 rounded-md border overflow-hidden",
                {
                    "border-gray-700": isOpen,
                    "border-transparent hover:border-gray-700": !isOpen,
                },
                className
            )}
        >
            {/* Header */}
            <div
                ref={headerRef}
                className={classNames(
                    "flex gap-1 select-none cursor-pointer relative group text-gray-0",
                    {
                        "bg-gray-700": isOpen,
                        "hover:bg-gray-700": !isOpen,
                        "py-2": !subtitle,
                        "py-2.5": subtitle,
                    }
                )}
                style={{
                    paddingTop: headerVerticalPadding,
                    paddingBottom: headerVerticalPadding,
                }}
                onClick={() => {
                    const opening = !isOpen
                    setIsOpen(opening)
                    if (opening && setIsToggled && !isToggled && !isHeaderToggleDisabled)
                        setIsToggled(true)
                }}
            >
                {/* Dropdown Arrow */}
                <Icons.Icon
                    Icon={Icons.ArrowDropdownRight}
                    className={classNames(animationClass, "cursor-pointer", {
                        "rotate-90": isOpen,
                        "text-white-400 group-hover:text-gray-0": !isOpen,
                    })}
                />

                {/* Title, Subtitle, Toggle */}
                <div className="flex items-center justify-between w-full mr-2.5">
                    {/* Title & Subtitle */}
                    {customHeaderComponent ? (
                        customHeaderComponent
                    ) : (
                        <div
                            className={classNames({
                                "my-0.5": !!subtitle,
                                "text-white-400 group-hover:text-gray-0": mutedTitle && !isOpen,
                            })}
                        >
                            <h5>{title}</h5>
                            {subtitle && <small className="text-gray-300 mono">{subtitle}</small>}
                        </div>
                    )}

                    {/* Toggle */}
                    {isToggled !== undefined ? (
                        <ToggleSwitch
                            isToggled={isToggled}
                            setIsToggled={setIsToggled!}
                            shouldStopPropogation
                        />
                    ) : (
                        RightItem
                    )}
                </div>
            </div>

            {/* Body */}
            <div className={classNames("w-full flex flex-col", { "px-2.5": !hasSubsections })}>
                <div
                    className={classNames(
                        "overflow-visible transform transition-all duration-150 will-change-transform",
                        { "py-2.5": isOpen && shouldPadChildrenVertically }
                    )}
                    style={{
                        height: contentHeight,
                    }}
                >
                    <div ref={contentRef} className="flex flex-col gap-2">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}
